"use client";

import { useEffect } from "react";
import { reportClientError } from "@/lib/report-client-error";

/**
 * Shared by error.tsx and global-error.tsx.
 *
 * Inline styles and a self-contained <style> block, not Tailwind: Next's docs
 * are explicit that global-error renders its own document and does NOT include
 * the app's global styles, so a class-based fallback would render unstyled
 * exactly when the app is most broken. For the same reason the colour scheme
 * here follows the OS rather than the app's theme toggle — the toggle's class
 * never reaches this document.
 *
 * The palette is copied from globals.css: cream #f5efe0 / ink #16233b, coral
 * #e5544a. Copied deliberately, since the point is to depend on nothing.
 */
export function ErrorFallback({
  error,
  retry,
}: {
  error: Error & { digest?: string };
  retry: () => void;
}) {
  useEffect(() => {
    reportClientError({
      name: error.name,
      // The digest is how a client-side render failure is matched to the
      // SERVER row for the same throw: in production React withholds the real
      // message from the browser and forwards only this id.
      message: error.digest ? `${error.message} [digest ${error.digest}]` : error.message,
      stack: error.stack ?? null,
    });
  }, [error]);

  return (
    <div className="rk-error">
      <style>{`
        .rk-error {
          min-height: 100dvh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
          background: #f5efe0;
          color: #16233b;
          font-family: system-ui, -apple-system, "Segoe UI", sans-serif;
        }
        .rk-error-card { max-width: 420px; text-align: center; }
        .rk-error h1 { font-size: 20px; font-weight: 700; margin: 0 0 8px; }
        .rk-error p { font-size: 15px; line-height: 1.5; margin: 0 0 20px; opacity: 0.7; }
        .rk-error button {
          appearance: none;
          border: 0;
          border-radius: 999px;
          padding: 11px 22px;
          font-size: 15px;
          font-weight: 600;
          color: #f5efe0;
          background: #e5544a;
          cursor: pointer;
        }
        .rk-error button:active { transform: scale(0.97); }
        @media (prefers-color-scheme: dark) {
          .rk-error { background: #16233b; color: #f5efe0; }
          .rk-error button { background: #f0685e; color: #16233b; }
        }
      `}</style>
      <div className="rk-error-card">
        <h1>Something went wrong</h1>
        <p>This page hit an error. It has been reported — trying again often works.</p>
        <button onClick={() => retry()}>Try again</button>
      </div>
    </div>
  );
}
